import {
  isVerifiedMakeStrict,
  canonicalForMake,
  isAcceptableMake,
  isPendingMakeRequest,
} from "./brand-registry";
import {
  DESCRIPTION_MIN,
  FIELD_ALIASES,
  MILEAGE_MAX,
  PHOTO_MAX,
  PHOTO_MIN,
  YEAR_MIN,
  getYearMax,
} from "./types";
import type {
  ConditionalContext,
  HostPublishContext,
  ListingFields,
  SuggestedPricing,
  ValidateConditionalOptions,
  ValidationError,
  ValuationContext,
} from "./types";
import { computeSuggestedPricing } from "./suggested-pricing";

/** Normalise a payload by collapsing legacy aliases onto canonical keys. */
export function normaliseListingFields<T extends Record<string, unknown>>(
  fields: T,
): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [key, val] of Object.entries(fields)) {
    if (val === undefined) continue;
    const canonical = FIELD_ALIASES[key] ?? key;
    out[canonical] = val;
  }
  return out;
}

function trimStr(v: unknown): string {
  if (v == null) return "";
  return String(v).trim();
}

function toNum(v: unknown): number | null {
  if (v == null || v === "") return null;
  const n = typeof v === "number" ? v : Number(v);
  return Number.isFinite(n) ? n : null;
}

/**
 * True when the given free-form make string resolves to a slug from the
 * verified car-brand list (sourced from the `car_makes` table via the
 * brand registry — see `./brand-registry.ts`). This is the single guard
 * that prevents new typo'd or free-text makes (`peugeots`, `BMW `, "mercedes
 * benz" with weird casing, etc.) from being persisted on `cars.make` once the
 * verified brand picker is in place. Backend save endpoints use this to
 * reject any payload that the frontend picker shouldn't have produced.
 *
 * Empty / null inputs return false — call sites should branch on
 * "missing make" separately so the user gets the appropriate error.
 */
export function isVerifiedMake(make: string | null | undefined): boolean {
  return isVerifiedMakeStrict(make);
}

/**
 * Returns the canonical display name for a verified make, or `null` if the
 * input doesn't strictly match the verified brand list. Save endpoints call
 * this to normalise `cars.make` (so we persist "Mercedes-Benz", not
 * "mercedes benz" or "MERCEDES").
 */
export function canonicaliseVerifiedMake(make: string | null | undefined): string | null {
  return canonicalForMake(make);
}

/**
 * Builds the canonical "make must be picked from the verified brand list"
 * error. Centralised so the API, wizard, and edit page all surface the same
 * copy.
 */
export const UNVERIFIED_MAKE_ERROR =
  "Pick a make from the verified brand list (or request a new one).";

/**
 * Stricter publish-time variant of {@link UNVERIFIED_MAKE_ERROR}. Used by
 * the admin moderate-approve endpoint when the listing's make is still on
 * the pending-request queue — the host's draft is fine but the brand must
 * be approved first before the listing can go live (task #1547).
 */
export const PENDING_MAKE_NOT_PUBLISHABLE_ERROR =
  "This make is awaiting brand-team approval. Approve the brand request first.";

/**
 * Documented draft minimums. A draft can be saved with just enough info to
 * generate a slug and identify the car. Also enforces that the make resolves
 * to a verified brand slug OR an active brand request — even drafts must
 * reference a known/requested brand so the database never accumulates a
 * fresh wave of typo'd makes after the picker ships.
 */
export function validateDraft(fields: ListingFields): ValidationError[] {
  const errors: ValidationError[] = [];
  const make = trimStr(fields.make);
  if (!make) {
    errors.push({ field: "make", message: "Make is required" });
  } else if (!isAcceptableMake(make)) {
    errors.push({ field: "make", message: UNVERIFIED_MAKE_ERROR });
  }
  // Model is now part of the draft minimum (was previously deferred to
  // publish-time). The wizard's VIN decoder fills this in for US-market
  // vehicles, but most non-US classics come back from NHTSA without a
  // model — without a draft-level gate the host could click straight
  // through, fill in everything else, and only discover the missing
  // model when /submit rejected with the generic "Model is required"
  // toast (which then bounced them all the way back to the VIN entry
  // step, with no visible cue that *model* was the actual problem).
  // Validating it here makes the Next button on vehicle-identity refuse
  // to advance until the host has entered a model, and the same rule
  // is enforced server-side on every save.
  const model = trimStr(fields.model);
  if (!model) {
    errors.push({ field: "model", message: "Model is required" });
  }
  const yearN = toNum(fields.year);
  const yearMax = getYearMax();
  if (!yearN || yearN < YEAR_MIN || yearN > yearMax) {
    // Bug L17 (Task #1943): tag the error with `code: "year_invalid"` so
    // POST /listings (and any future per-field UI) can surface a stable
    // machine-readable code instead of pattern-matching on the message
    // string. Without this, payloads like `year: -1` were rejected as
    // "A valid year is required" but the API consumer had no easy way
    // to distinguish a year-range failure from a missing field.
    errors.push({
      field: "year",
      code: "year_invalid",
      message:
        !yearN || yearN < YEAR_MIN
          ? "A valid year is required"
          : `Year must be ${yearMax} or earlier`,
    });
  }
  // Mileage is optional on draft (it's collected later in the wizard) but
  // when present we still apply the upper bound so a typo can't slip into
  // an early autosave and survive to publish via per-field PUTs that don't
  // re-touch mileage. (#1595)
  const mileageDraft = toNum(fields.mileage);
  if (mileageDraft != null && mileageDraft > MILEAGE_MAX) {
    errors.push({
      field: "mileage",
      message: `Mileage looks too high (max ${MILEAGE_MAX.toLocaleString("en-US")})`,
    });
  }
  return errors;
}

/**
 * Maps each conditionally-required dependent field to the toggle that controls
 * its requirement. Used by `validateConditional` to expand a change-scoped
 * save's field set: when a host edits a dependent field on its own (e.g. clears
 * `price_driving`), its owning toggle is pulled into scope so the
 * "enabled but no rate set" rule still fires. Both legacy and current field
 * name variants are listed because `validateConditional` reads either.
 */
const DEPENDENT_FIELD_TO_TOGGLE: Readonly<Record<string, string>> = {
  // Self-drive
  price_driving: "rental_driving_active",
  // Chauffeured
  chauffeured_hourly_rate: "rental_chauffeured_active",
  chauffeured_min_price_hourly_rate: "rental_chauffeured_active",
  // Event
  price_event: "rental_event_active",
  event_hourly_rate: "rental_event_active",
  event_min_price_hourly_rate: "rental_event_active",
  // Delivery
  self_drive_delivery_enabled: "delivery_enabled",
  delivery_free: "delivery_enabled",
  delivery_fee_per_unit: "delivery_enabled",
  delivery_radius_full: "delivery_enabled",
  delivery_radius_free: "delivery_enabled",
  delivery_maximum_distance: "delivery_enabled",
  // Mileage limit
  mileage_limit_miles: "mileage_limit",
  mileage_limit_per_day: "mileage_limit",
  mileage_overage_rate: "mileage_limit",
  overage_rate_per_mile: "mileage_limit",
  // Instant book
  instant_book_mode: "instant_rental",
  instantBookDates: "instant_rental",
};

/**
 * Conditional ("required-by-context") rules. A field is required only when
 * another toggle on the same listing makes it meaningful — e.g. chauffeured
 * pricing is only required when chauffeured rental is enabled.
 *
 * On the publish path these rules fire unconditionally so a host cannot
 * publish with a half-configured rental type, delivery option, or mileage
 * limit. On the draft-save path the API server passes
 * `opts.draftFieldsInPayload` so rules whose controlling toggle is not in
 * the request payload are skipped (task #1584 — Save & Exit from an early
 * wizard step shouldn't surface pricing/delivery errors the host never
 * touched).
 */
export function validateConditional(
  fields: ListingFields,
  ctx?: ConditionalContext,
  valuation?: ValuationContext,
  opts?: ValidateConditionalOptions,
): ValidationError[] {
  const errors: ValidationError[] = [];
  // Change-scoped saves (draft/edit) pass `draftFieldsInPayload` = the fields
  // the host actually changed. A dependent field edited on its OWN — e.g.
  // clearing `price_driving` while `rental_driving_active` stays on, or
  // emptying `chauffeured_hourly_rate`/`mileage_limit_per_day` — must still
  // pull its controlling toggle into scope. Otherwise `toggleAllowed(toggle)`
  // below returns false and the "enabled but no rate/limit set" rule is
  // silently skipped, letting an active listing save half-configured. Expand
  // the scope with each dirty dependent field's owning toggle. Publish-time
  // callers pass no filter (`inPayload` undefined) so every rule still fires
  // unconditionally — this only affects the scoped (edit-save) path.
  const rawInPayload = opts?.draftFieldsInPayload;
  const inPayload = rawInPayload ? new Set(rawInPayload) : undefined;
  if (inPayload) {
    for (const field of rawInPayload!) {
      const toggle = DEPENDENT_FIELD_TO_TOGGLE[field];
      if (toggle) inPayload.add(toggle);
    }
  }
  const toggleAllowed = (key: string): boolean => !inPayload || inPayload.has(key);

  // ---- Self-drive ---------------------------------------------------------
  if (fields.rental_driving_active && toggleAllowed("rental_driving_active")) {
    const p = toNum(fields.price_driving);
    if (!p || p <= 0) {
      errors.push({
        field: "price_driving",
        message: "Self-drive hire is enabled but no daily rate is set",
      });
    }
  }

  // ---- Chauffeured --------------------------------------------------------
  if (fields.rental_chauffeured_active && toggleAllowed("rental_chauffeured_active")) {
    const hr = toNum(fields.chauffeured_hourly_rate);
    if (!hr || hr <= 0) {
      errors.push({
        field: "chauffeured_hourly_rate",
        message: "Chauffeured hire is enabled but no hourly rate is set",
      });
    }
    const min = toNum(fields.chauffeured_min_price_hourly_rate);
    if (!min || min <= 0) {
      errors.push({
        field: "chauffeured_min_price_hourly_rate",
        message:
          "Chauffeured hire is enabled but no minimum price per booking is set",
      });
    }
    // Cross-field guardrail (inbox feedback, May 2026): a host set
    // `chauffeured_hourly_rate = 350` and `min_price_per_booking = 100`,
    // which is logically inconsistent — the minimum is supposed to be a
    // floor that protects the host on short bookings, not a value the
    // hourly rate already clears on a 1-hour booking. With `min < hourly`
    // the minimum has zero effect (any 1-hour booking already bills above
    // it) and is almost always a typo or a misunderstanding of the field.
    // Require `min >= hourly` so the field actually means what its label
    // says ("minimum"). Only fires when BOTH values are >0 — the
    // "missing" rules above already cover the empty cases and we don't
    // want to double-report.
    if (hr != null && hr > 0 && min != null && min > 0 && min < hr) {
      errors.push({
        field: "chauffeured_min_price_hourly_rate",
        message:
          "Minimum price per booking can't be less than the hourly rate",
      });
    }
  }

  // ---- Event --------------------------------------------------------------
  // The daily rate is the required base price for event hire. The hourly /
  // minimum-price fields are *optional* pricing modifiers — when no hourly
  // rate is set the system derives one from `daily ÷ 8`, and the minimum is
  // simply not applied.
  if (fields.rental_event_active && toggleAllowed("rental_event_active")) {
    const p = toNum(fields.price_event);
    if (!p || p <= 0) {
      errors.push({
        field: "price_event",
        message: "Event hire is enabled but no daily rate is set",
      });
    }
    // Note: event types are no longer required to enable event hire — the
    // wizard never exposed an event-type picker, so requiring at least one
    // selection blocked hosts on a field they couldn't set. The
    // `eventTypeCount` ctx field is still accepted for backwards compat
    // but no rule reads it. (Per host feedback, May 2026.)
    //
    // Event hourly / min-price are both optional, but if a host opts in
    // to BOTH, the same logical constraint as chauffeured applies: the
    // minimum is a floor, so it has to be at or above the hourly rate or
    // it never bites. Skip the rule entirely when either side is blank.
    const eventHr = toNum(fields.event_hourly_rate);
    const eventMin = toNum(fields.event_min_price_hourly_rate);
    if (eventHr != null && eventHr > 0 && eventMin != null && eventMin > 0 && eventMin < eventHr) {
      errors.push({
        field: "event_min_price_hourly_rate",
        message:
          "Minimum price per booking can't be less than the hourly rate",
      });
    }
  }

  // ---- At least one rental type ------------------------------------------
  // Only enforce when at least one rental toggle is part of the request
  // payload (or no payload filter is provided — i.e. a publish-time call).
  // Without a payload filter the older `!= null` check let the rule fire on
  // merged DB rows where the toggle columns default to `false`, which is
  // the bug at the heart of task #1584.
  const anyRentalToggleInPayload = inPayload
    ? inPayload.has("rental_driving_active") ||
      inPayload.has("rental_chauffeured_active") ||
      inPayload.has("rental_event_active")
    : fields.rental_driving_active != null ||
      fields.rental_chauffeured_active != null ||
      fields.rental_event_active != null;
  if (anyRentalToggleInPayload) {
    const noneActive =
      !fields.rental_driving_active &&
      !fields.rental_chauffeured_active &&
      !fields.rental_event_active;
    if (noneActive) {
      errors.push({
        field: "rental_driving_active",
        message: "At least one rental type must be enabled",
      });
    }
  }

  // ---- Delivery aggregate capability --------------------------------------
  // `delivery_enabled` remains a compatibility aggregate. It is true when
  // self-drive delivery is opted in or Chauffeured/Event is active.
  const deliveryGateRelevant =
    !inPayload ||
    inPayload.has("rental_chauffeured_active") ||
    inPayload.has("rental_event_active") ||
    inPayload.has("self_drive_delivery_enabled") ||
    inPayload.has("delivery_enabled");

  // ---- Delivery -----------------------------------------------------------
  const deliveryOffered =
    !!fields.self_drive_delivery_enabled ||
    !!fields.delivery_enabled ||
    !!fields.rental_chauffeured_active ||
    !!fields.rental_event_active;
  // The per-distance fee is always required when any rental mode offers
  // delivery (unless
  // "Free delivery" covers the full radius). Max delivery distance is always
  // optional — blank means delivery is offered anywhere. Free radius can't
  // exceed max distance when both are set.
  if (deliveryGateRelevant && deliveryOffered) {
    if (!fields.delivery_free) {
      const fee = toNum(fields.delivery_fee_per_unit);
      if (!fee || fee <= 0) {
        errors.push({
          field: "delivery_fee_per_unit",
          message: "Delivery is enabled but no per-distance fee is set",
        });
      }
    }
    const radius = toNum(fields.delivery_radius_full ?? fields.delivery_maximum_distance);
    const free = toNum(fields.delivery_radius_free);
    if (free != null && radius != null && free > radius) {
      errors.push({
        field: "delivery_radius_free",
        message: "Free delivery distance can't exceed the maximum delivery distance",
      });
    }
  }

  // ---- Mileage limit ------------------------------------------------------
  if (fields.mileage_limit && toggleAllowed("mileage_limit")) {
    const miles = toNum(fields.mileage_limit_miles ?? fields.mileage_limit_per_day);
    if (!miles || miles <= 0) {
      errors.push({
        field: "mileage_limit_miles",
        message: "Mileage limit is enabled but no per-day distance is set",
      });
    }
    const rate = toNum(fields.mileage_overage_rate ?? fields.overage_rate_per_mile);
    if (!rate || rate <= 0) {
      errors.push({
        field: "mileage_overage_rate",
        message: "Mileage limit is enabled but no overage rate is set",
      });
    }
  }

  // ---- Instant book "selected dates" -------------------------------------
  if (
    fields.instant_rental &&
    toggleAllowed("instant_rental") &&
    fields.instant_book_mode === "selected" &&
    ctx &&
    typeof ctx.instantBookDateCount === "number" &&
    ctx.instantBookDateCount <= 0
  ) {
    errors.push({
      field: "instantBookDates",
      message:
        "Instant Book is set to specific dates but no dates have been added",
    });
  }

  // ---- Absolute price ceiling (May 2026 — car #1059383 incident) ---------
  // Hard cap on every price/rate field, applied regardless of the
  // valuation-based pricing guardrails below (which only fire when an
  // insured value + suggested-pricing snapshot are available). A host
  // typo like `435,345,234` in `price_event` would otherwise slip
  // through on a car without an insured value and end up rendered as
  // "from DKK 435,345,234/day" on the listing card. The cap is set
  // deliberately far above any real-world classic-car rate (10M minor
  // units ≈ $1.4M USD/day) so it only ever catches keysmash typos.
  // Gated on `toggleAllowed` so a Save & Exit from a step that doesn't
  // touch pricing won't be retroactively blocked by an old bad row.
  const ABS_PRICE_CEILING = 10_000_000;
  const PRICE_FIELDS: ReadonlyArray<{ key: keyof ListingFields; label: string }> = [
    { key: "price_driving", label: "Self-drive daily rate" },
    { key: "price_event", label: "Event daily rate" },
    { key: "chauffeured_hourly_rate", label: "Chauffeured hourly rate" },
    { key: "chauffeured_min_price_hourly_rate", label: "Chauffeured minimum price" },
    { key: "event_hourly_rate", label: "Event hourly rate" },
    { key: "event_min_price_hourly_rate", label: "Event minimum price" },
    { key: "delivery_fee_per_unit", label: "Delivery fee per unit" },
  ];
  for (const { key, label } of PRICE_FIELDS) {
    if (!toggleAllowed(key as string)) continue;
    const v = toNum(fields[key]);
    if (v != null && v > ABS_PRICE_CEILING) {
      errors.push({
        field: key as string,
        message: `${label} is unrealistically high (max ${ABS_PRICE_CEILING.toLocaleString("en-US")})`,
      });
    }
  }

  // ---- Unified price guardrails (task #1540) ------------------------------
  // Self-drive and event have **no** hard minimum — only an above-ceiling
  // soft warning that triggers the admin review flag. Chauffeured-hourly
  // keeps a hard minimum because it folds in Task #1477's per-currency
  // floor (driver labour cost). All bounds come from the same unified
  // `computeSuggestedPricing` the AI route uses. Bounds source priority:
  //   1. `valuation.precomputedPricing`     (parity with /suggested-rate)
  //   2. `valuation.pricingInput`           (recompute with full inputs)
  //   3. `{ insuredValue, countryCode }`    (valuation-only fallback)
  let pricing: SuggestedPricing | null = null;
  if (valuation?.precomputedPricing) {
    pricing = valuation.precomputedPricing;
  } else if (valuation?.pricingInput) {
    pricing = computeSuggestedPricing(valuation.pricingInput);
  } else if (valuation?.insuredValue && valuation.insuredValue > 0) {
    pricing = computeSuggestedPricing({
      insuredValue: valuation.insuredValue,
      countryCode: valuation.countryCode ?? null,
    });
  }
  if (pricing && valuation) {
    const inPayload = valuation.pricingFieldsInPayload;

    // Chauffeured hourly is the **only** pricing field with a host-facing
    // hard minimum (per-currency driver-cost floor). Above-ceiling values
    // are detected separately by `detectAbovePricingCeiling()` for admin
    // moderation only — they do NOT surface a host-visible message.
    if (fields.rental_chauffeured_active && (!inPayload || inPayload.has("chauffeured_hourly_rate"))) {
      const hr = toNum(fields.chauffeured_hourly_rate);
      const sym = valuation.currencySymbol ?? "$";
      if (hr != null && hr > 0 && hr < pricing.minHourlyChauffeured) {
        errors.push({
          field: "chauffeured_hourly_rate",
          message: `Chauffeured hourly rate must be at least ${sym}${pricing.minHourlyChauffeured}/hr`,
          severity: "error",
        });
      }
    }
  }

  return errors;
}

/**
 * **Admin-only** detector for prices that exceed the unified ceiling. These
 * are NOT surfaced as host-facing warnings (per task #1540); the API server
 * uses them to insert a `PRICING_ABOVE_CEILING` moderation log + admin
 * notification, and the host save still completes normally.
 */
export function detectAbovePricingCeiling(
  fields: ListingFields,
  pricing: SuggestedPricing,
  pricingFieldsInPayload?: ReadonlySet<string>,
): Array<{ field: string; message: string }> {
  const flags: Array<{ field: string; message: string }> = [];
  const inPayload = pricingFieldsInPayload;
  if (fields.rental_driving_active && (!inPayload || inPayload.has("price_driving"))) {
    const p = toNum(fields.price_driving);
    if (p != null && p > 0 && p > pricing.maxDailySelfDrive) {
      flags.push({ field: "price_driving", message: `${p} exceeds self-drive ceiling ${pricing.maxDailySelfDrive}` });
    }
  }
  if (fields.rental_event_active && (!inPayload || inPayload.has("price_event"))) {
    const p = toNum(fields.price_event);
    if (p != null && p > 0 && p > pricing.maxDailyEvent) {
      flags.push({ field: "price_event", message: `${p} exceeds event ceiling ${pricing.maxDailyEvent}` });
    }
  }
  if (fields.rental_chauffeured_active && (!inPayload || inPayload.has("chauffeured_hourly_rate"))) {
    const hr = toNum(fields.chauffeured_hourly_rate);
    if (hr != null && hr > 0 && hr > pricing.maxHourlyChauffeured) {
      flags.push({ field: "chauffeured_hourly_rate", message: `${hr} exceeds chauffeured hourly ceiling ${pricing.maxHourlyChauffeured}` });
    }
  }
  return flags;
}

/**
 * Static map of toggle-driven required fields. The predicate answers "is this
 * field required *right now*, given the current toggles?" without looking at
 * the field's own value. This is what `OptionalHint` uses so the "(Optional)"
 * hint stays hidden after the host enters a value.
 */
const CONDITIONAL_REQUIREMENTS: Readonly<Record<string, (f: ListingFields) => boolean>> = {
  price_driving: (f) => !!f.rental_driving_active,
  chauffeured_hourly_rate: (f) => !!f.rental_chauffeured_active,
  chauffeured_min_price_hourly_rate: (f) => !!f.rental_chauffeured_active,
  price_event: (f) => !!f.rental_event_active,
  delivery_enabled: (f) =>
    !!f.self_drive_delivery_enabled ||
    !!f.rental_chauffeured_active ||
    !!f.rental_event_active,
  delivery_fee_per_unit: (f) =>
    (!!f.self_drive_delivery_enabled ||
      !!f.delivery_enabled ||
      !!f.rental_chauffeured_active ||
      !!f.rental_event_active) && !f.delivery_free,
  mileage_limit_miles: (f) => !!f.mileage_limit,
  mileage_limit_per_day: (f) => !!f.mileage_limit,
  mileage_overage_rate: (f) => !!f.mileage_limit,
  overage_rate_per_mile: (f) => !!f.mileage_limit,
};

/**
 * True when a field is required-by-context (a toggle on the same listing
 * makes it mandatory), independent of the field's current value. Use this in
 * UI hint components.
 */
export function isConditionallyRequired(
  field: string,
  fields: ListingFields,
): boolean {
  const fn = CONDITIONAL_REQUIREMENTS[field];
  return fn ? fn(fields) : false;
}

/**
 * True when `field` is required in the given context, taking conditional
 * (toggle-driven) rules into account. Used by hint components so they don't
 * show "(Optional)" against a field that is in fact required right now.
 */
export function isFieldRequired(
  field: string,
  fields?: ListingFields,
  _ctx?: ConditionalContext,
): boolean {
  if (PUBLISH_REQUIRED_FIELDS.has(field)) return true;
  if (!fields) return false;
  return isConditionallyRequired(field, fields);
}

/**
 * Stable error message for the "host phone is not verified" gate. Surfaced
 * verbatim by the wizard, the dashboard's submit-for-review CTA, and the
 * pause/resume toggle so the copy never drifts between surfaces.
 */
export const HOST_PHONE_UNVERIFIED_ERROR =
  "Verify your phone number before publishing this listing.";

/** Field name used on the structured `host_phone_unverified` API error. */
export const HOST_PHONE_FIELD = "host_phone";

/**
 * Publish/submit minimums. These are the gates the wizard's preview step,
 * the edit page, and PUT /listings/:id/submit all enforce.
 *
 * `photoCount` should be the count of active photos in `cars_images`.
 *
 * The optional `hostCtx` parameter folds in host-side publish requirements
 * (verified phone, etc.) — see {@link HostPublishContext}. Call sites that
 * do NOT want to enforce the host-side gate (plain saves of already-live
 * listings, the wizard's draft-save) must omit it. Submit + resume call
 * sites must pass it.
 */
export function validateForPublish(
  fields: ListingFields,
  photoCount: number,
  ctx?: ConditionalContext,
  hostCtx?: HostPublishContext,
  valuation?: ValuationContext,
  opts?: ValidateConditionalOptions,
): ValidationError[] {
  return validateRequiredContentFields(fields, photoCount, ctx, valuation, hostCtx, {
    allowPendingMake: false,
  }, opts);
}

/**
 * Shared body of {@link validateForPublish} and
 * {@link validateForWizardSubmit}. The two public entry points only differ
 * in whether a pending-make-request is acceptable: publish (strictest gate)
 * rejects it with {@link PENDING_MAKE_NOT_PUBLISHABLE_ERROR}; wizard submit
 * accepts it and lets admin moderate-approve enforce strict.
 */
function validateRequiredContentFields(
  fields: ListingFields,
  photoCount: number,
  ctx: ConditionalContext | undefined,
  valuation: ValuationContext | undefined,
  hostCtx: HostPublishContext | undefined,
  opts: { allowPendingMake: boolean },
  condOpts?: ValidateConditionalOptions,
): ValidationError[] {
  const errors: ValidationError[] = [];

  const make = trimStr(fields.make);
  if (!make) {
    errors.push({ field: "make", message: "Make is required" });
  } else if (opts.allowPendingMake) {
    if (!isAcceptableMake(make)) {
      // Wizard submit flips the listing to `awaiting_approval`, not `active`;
      // pending brand requests are valid here for the same reason as
      // validateDraft above. Admin moderate-approve enforces strict.
      errors.push({ field: "make", message: UNVERIFIED_MAKE_ERROR });
    }
  } else if (!isVerifiedMakeStrict(make)) {
    // Publish is the strictest gate (#1547). Pending-request makes are
    // accepted by `validateDraft` and `validateForWizardSubmit` so the
    // host can keep building, but they MUST NOT reach `active`. Emit a
    // distinct, publish-specific error so the admin moderate-approve UI
    // can surface "awaiting brand verification" copy instead of the
    // generic "pick a verified make" message.
    if (isPendingMakeRequest(make)) {
      errors.push({ field: "make", message: PENDING_MAKE_NOT_PUBLISHABLE_ERROR });
    } else {
      errors.push({ field: "make", message: UNVERIFIED_MAKE_ERROR });
    }
  }
  if (!trimStr(fields.model)) {
    errors.push({ field: "model", message: "Model is required" });
  }
  const yearN = toNum(fields.year);
  const yearMax = getYearMax();
  if (!yearN || yearN < YEAR_MIN || yearN > yearMax) {
    errors.push({
      field: "year",
      message:
        !yearN || yearN < YEAR_MIN
          ? "A valid year is required"
          : `Year must be ${yearMax} or earlier`,
    });
  }
  if (!trimStr(fields.color) && !trimStr(fields.colour_name)) {
    errors.push({ field: "color", message: "Colour is required" });
  }
  const mileageN = toNum(fields.mileage);
  if (mileageN == null || mileageN <= 0) {
    errors.push({ field: "mileage", message: "Mileage is required" });
  } else if (mileageN > MILEAGE_MAX) {
    errors.push({
      field: "mileage",
      message: `Mileage looks too high (max ${MILEAGE_MAX.toLocaleString("en-US")})`,
    });
  }
  const conditionN = toNum(fields.condition);
  if (!conditionN || conditionN <= 0) {
    errors.push({ field: "condition", message: "Condition is required" });
  }
  if (!trimStr(fields.city)) {
    errors.push({ field: "city", message: "City is required" });
  }
  // State / county is required for US listings only. Most non-US markets
  // (e.g. GB, much of the EU) address by city + postal code without a
  // province/county field, so requiring it there blocked otherwise-complete
  // non-US listings (task #4401). Country is always required, so an empty
  // country still fails below.
  const countryForState = trimStr(fields.country_code).toUpperCase();
  if (countryForState === "US" && !trimStr(fields.state)) {
    errors.push({ field: "state", message: "State is required" });
  }
  if (!trimStr(fields.country_code)) {
    errors.push({ field: "country_code", message: "Country is required" });
  }
  const desc = trimStr(fields.description);
  if (desc.length < DESCRIPTION_MIN) {
    errors.push({
      field: "description",
      message: `Description must be at least ${DESCRIPTION_MIN} characters (${desc.length} so far)`,
    });
  }
  if (photoCount < PHOTO_MIN) {
    errors.push({
      field: "photos",
      message: `At least ${PHOTO_MIN} photos required (${photoCount} uploaded)`,
    });
  }
  if (photoCount > PHOTO_MAX) {
    errors.push({
      field: "photos",
      message: `Maximum ${PHOTO_MAX} photos allowed (${photoCount} uploaded)`,
    });
  }

  // Fold in every toggle-driven rule (including valuation guardrails).
  // When the caller scopes the save to specific payload fields
  // (`condOpts.draftFieldsInPayload`), the conditional rules whose
  // controlling toggle is not part of the edit are skipped — so a partial
  // pricing/rules edit on a live (or imported) listing isn't blocked by
  // unrelated all-false rental flags merged in from the DB row.
  for (const e of validateConditional(fields, ctx, valuation, condOpts)) errors.push(e);

  // Host-side publish requirements (task #1007). The platform_config
  // `host_publish_requirements.requires_phone` kill switch decides whether
  // the gate is enforced at all; call sites pass the resolved flag in
  // `hostCtx.requirePhoneVerified` so this helper stays free of platform
  // lookups. The wizard's draft-save and the plain PUT /listings/:id route
  // intentionally omit `hostCtx` so that already-active listings are not
  // retroactively blocked from edits.
  if (hostCtx?.requirePhoneVerified && !hostCtx.hostPhoneVerified) {
    errors.push({
      field: HOST_PHONE_FIELD,
      message: HOST_PHONE_UNVERIFIED_ERROR,
    });
  }

  return errors;
}

/**
 * Wizard-submit gate. The wizard now collects pricing and delivery alongside
 * the core listing content (vehicle, description, photos, location). On
 * submission the listing transitions directly to `awaiting_approval`, so this
 * validator enforces both the unconditional content requirements AND the
 * conditional pricing/delivery rules via `validateConditional`.
 */
export function validateForWizardSubmit(
  fields: ListingFields,
  photoCount: number,
  ctx?: ConditionalContext,
  valuation?: ValuationContext,
  hostCtx?: HostPublishContext,
): ValidationError[] {
  return validateRequiredContentFields(fields, photoCount, ctx, valuation, hostCtx, {
    allowPendingMake: true,
  });
}

/**
 * Required field set for publish (canonical names). Useful for marking inputs
 * so that the wizard knows which inputs participate in the publish gate. The
 * wizard does NOT show "Required" labels — required-ness is communicated by
 * the amber inline error styling when validation fails. Optional fields, by
 * contrast, get a muted "Optional" hint.
 */
export const PUBLISH_REQUIRED_FIELDS: ReadonlySet<string> = new Set([
  "make",
  "model",
  "year",
  "color",
  "mileage",
  "condition",
  "city",
  "state",
  "country_code",
  "description",
  "photos",
]);

/**
 * Optional fields that show up in the wizard / edit page. Anything not here
 * and not in `PUBLISH_REQUIRED_FIELDS` is implicitly optional.
 *
 * Fields that become required-by-context (e.g. `chauffeured_hourly_rate` when
 * chauffeured hire is enabled) are still listed here because they ARE optional
 * when the toggle is off. Consumers should call `isFieldRequired(field, data)`
 * to find out whether they're currently required given the live form state.
 */
export const OPTIONAL_LISTING_FIELDS: ReadonlySet<string> = new Set([
  // Vehicle / description / location
  "submodel",
  "vin",
  "engine",
  "body_style",
  "category",
  "car_history",
  "history",
  "street",
  "zip",
  "transmission",
  "passengers",
  "is_convertible",
  "convertible",
  "has_aircon",
  "air_conditioning",
  "is_left_hand_drive",
  "left_hand_drive",
  "mileage_is_accurate",
  "all_seatbelts",
  // Pricing
  "rental_driving_min_duration",
  "driving_rental_min_duration",
  "chauffeured_hourly_rate",
  "chauffeured_min_price_hourly_rate",
  "event_hourly_rate",
  "event_min_price_hourly_rate",
  "event_types",
  "delivery_fee_min",
  "delivery_airports",
  // Rules
  "fuel_policy",
  "weather_policy",
  "mileage_limit",
  "mileage_limit_miles",
  "mileage_overage_rate",
  "pets_allowed",
  "allow_decorations",
  "pickup_instructions",
  "internal_notes",
  // Availability
  "custom_daily_rate",
  "blocked_dates",
]);

export function isOptionalField(field: string): boolean {
  if (PUBLISH_REQUIRED_FIELDS.has(field)) return false;
  return OPTIONAL_LISTING_FIELDS.has(field);
}

/**
 * Wizard step IDs map -> required publish fields belonging to that step. Used
 * to focus the right step when the API rejects a publish/save with structured
 * errors.
 */
export const WIZARD_STEP_FIELDS: Readonly<Record<string, ReadonlyArray<string>>> = {
  "vehicle-identity": ["make", "model", "year"],
  "colour-picker": ["color", "mileage", "condition"],
  "car-location": ["city", "state", "country_code"],
  "car-story": ["description"],
  "photos": ["photos"],
  "pricing": [
    "rental_driving_active",
    "price_driving",
    "rental_chauffeured_active",
    "chauffeured_hourly_rate",
    "chauffeured_min_price_hourly_rate",
    "rental_event_active",
    "price_event",
    "event_hourly_rate",
    "event_min_price_hourly_rate",
    "delivery_enabled",
    "delivery_fee_per_unit",
    "delivery_free",
    "delivery_radius_full",
    "delivery_radius_free",
    "eventTypeIds",
    "instantBookDates",
  ],
};

/** Edit-page tab IDs -> required publish fields (including conditional ones). */
export const EDIT_TAB_FIELDS: Readonly<Record<string, ReadonlyArray<string>>> = {
  "vehicle": ["make", "model", "year", "color", "mileage", "condition"],
  "description": ["description"],
  "location": ["city", "state", "country_code"],
  "photos": ["photos"],
  "pricing": [
    "rental_driving_active",
    "price_driving",
    "rental_chauffeured_active",
    "chauffeured_hourly_rate",
    "chauffeured_min_price_hourly_rate",
    "rental_event_active",
    "price_event",
    "event_hourly_rate",
    "event_min_price_hourly_rate",
    "delivery_enabled",
    "delivery_fee_per_unit",
    "delivery_free",
    "delivery_radius_full",
    "delivery_radius_free",
    "eventTypeIds",
    "instantBookDates",
  ],
  "rules": ["mileage_limit_miles", "mileage_overage_rate"],
};

/**
 * Fields owned by the wizard's dedicated Instant Book step (task #6425).
 * Only meaningful when the caller opts in via `instantBookOwnStep` —
 * with the feature flag OFF the wizard has no such step and these
 * fields still belong to "pricing" (legacy behaviour).
 */
const INSTANT_BOOK_STEP_FIELDS: ReadonlyArray<string> = ["instant_rental", "instant_book_mode", "instantBookDates"];

export function findWizardStepForField(
  field: string,
  opts?: { instantBookOwnStep?: boolean },
): string | null {
  if (opts?.instantBookOwnStep && INSTANT_BOOK_STEP_FIELDS.includes(field)) {
    return "instant-book";
  }
  for (const [step, fields] of Object.entries(WIZARD_STEP_FIELDS)) {
    if (fields.includes(field)) return step;
  }
  return null;
}

export function findEditTabForField(field: string): string | null {
  for (const [tab, fields] of Object.entries(EDIT_TAB_FIELDS)) {
    if (fields.includes(field)) return tab;
  }
  return null;
}
